'use client';

import mixpanel from 'mixpanel-browser';

const MIXPANEL_TOKEN = process.env.NEXT_PUBLIC_MIXPANEL_TOKEN || '';

let initialized = false;

export function initMixpanel() {
  if (initialized || !MIXPANEL_TOKEN || MIXPANEL_TOKEN === 'your-mixpanel-project-token-here') return;
  mixpanel.init(MIXPANEL_TOKEN, {
    track_pageview: false, // we handle manually for App Router
    persistence: 'localStorage',
    debug: process.env.NODE_ENV === 'development',
  });
  initialized = true;
}

export function trackEvent(eventName: string, properties?: Record<string, unknown>) {
  if (!initialized) return;
  mixpanel.track(eventName, properties);
}

export function trackPageView(pageName: string, properties?: Record<string, unknown>) {
  if (!initialized) return;
  mixpanel.track('Page View', {
    page: pageName,
    url: typeof window !== 'undefined' ? window.location.pathname : '',
    ...properties,
  });
}

export function identifyUser(userId: string, traits?: Record<string, unknown>) {
  if (!initialized) return;
  mixpanel.identify(userId);
  if (traits) {
    mixpanel.people.set(traits);
  }
}

export function resetUser() {
  if (!initialized) return;
  mixpanel.reset();
}

export function setUserProperties(properties: Record<string, unknown>) {
  if (!initialized) return;
  mixpanel.people.set(properties);
}

// ── Offer Acceptance Workflow Tracking ──────────────────────────────────────

export function trackOfferSent(properties: {
  offerId: string;
  contractorId: string;
  productionId: string;
  roleName: string;
  fee?: number;
  channel?: 'email' | 'sms' | 'both';
}) {
  trackEvent('Offer Sent', properties);
}

export function trackOfferViewed(properties: {
  offerId: string;
  contractorId?: string;
  productionId?: string;
  roleName?: string;
}) {
  trackEvent('Offer Viewed', properties);
}

export function trackOfferAccepted(properties: {
  offerId: string;
  contractorId: string;
  productionId: string;
  roleName: string;
  fee?: number;
  daysToAccept?: number;
}) {
  trackEvent('Offer Accepted', properties);
}

export function trackOfferDeclined(properties: {
  offerId: string;
  contractorId: string;
  productionId: string;
  roleName: string;
  reason?: string;
}) {
  trackEvent('Offer Declined', properties);
}

export function trackCounterOfferSubmitted(properties: {
  offerId: string;
  originalFee?: number;
  counterFee?: number;
}) {
  trackEvent('Counter Offer Submitted', properties);
}

// ── Contract Turnaround Funnel ───────────────────────────────────────────────

export function trackContractSent(properties: {
  contractId: string;
  contractorId: string;
  productionId: string;
  roleName: string;
  fee?: number;
  templateId?: string;
}) {
  trackEvent('Contract Sent', properties);
}

export function trackContractViewed(properties: {
  contractId: string;
  contractorId?: string;
}) {
  trackEvent('Contract Viewed', properties);
}

export function trackContractSigned(properties: {
  contractId: string;
  contractorId: string;
  productionId: string;
  roleName: string;
  daysToSign?: number;
}) {
  trackEvent('Contract Signed', properties);
}

export function trackW9Submitted(properties: {
  contractorId: string;
  productionId?: string;
}) {
  trackEvent('W9 Submitted', properties);
}

// ── Reminder Effectiveness ───────────────────────────────────────────────────

export function trackReminderSent(properties: {
  type: 'payment' | 'contract' | 'w9' | 'call_sheet' | 'bio_headshot' | 'custom_sms';
  channel: 'email' | 'sms' | 'both';
  contractorId?: string;
  contractId?: string;
  productionId?: string;
  isBulk?: boolean;
  bulkCount?: number;
}) {
  trackEvent('Reminder Sent', properties);
}

export function trackReminderOpened(properties: {
  type: string;
  channel: 'email' | 'sms';
  contractorId?: string;
}) {
  trackEvent('Reminder Opened', properties);
}

export function trackReminderActedOn(properties: {
  type: string;
  channel: 'email' | 'sms';
  contractorId?: string;
  actionTaken?: string;
}) {
  trackEvent('Reminder Acted On', properties);
}

// ── Payment Tracking ─────────────────────────────────────────────────────────

export function trackPaymentInitiated(properties: {
  contractId: string;
  contractorId: string;
  amount: number;
  productionId?: string;
}) {
  trackEvent('Payment Initiated', properties);
}

export function trackPaymentCompleted(properties: {
  contractId: string;
  contractorId: string;
  amount: number;
  productionId?: string;
  stripePaymentIntentId?: string;
}) {
  trackEvent('Payment Completed', properties);
}

// ── Onboarding Funnel ────────────────────────────────────────────────────────

export function trackOnboardingStepCompleted(properties: {
  stepKey: string;
  stepLabel: string;
  contractorId?: string;
  productionId?: string;
  offerId?: string;
}) {
  trackEvent('Onboarding Step Completed', properties);
}

export function trackOnboardingCompleted(properties: {
  contractorId?: string;
  productionId?: string;
  offerId?: string;
  totalSteps?: number;
}) {
  trackEvent('Onboarding Completed', properties);
}

// ── Compliance Funnel Tracking ────────────────────────────────────────────────

export function trackW9FunnelStep(properties: {
  step: 'requested' | 'reminder_sent' | 'received' | 'approved' | 'rejected';
  contractorId: string;
  productionId?: string;
  contractId?: string;
  daysElapsed?: number;
}) {
  trackEvent('W9 Funnel Step', properties);
}

export function trackW9ThresholdCrossed(properties: {
  contractorId: string;
  totalPaid: number;
  threshold: number;
  productionId?: string;
  contractCount?: number;
}) {
  trackEvent('1099 Threshold Crossed', {
    ...properties,
    thresholdLabel: `$${properties.threshold}`,
  });
}

export function trackComplianceViolation(properties: {
  type: 'w9_missing' | 'w9_expired' | '1099_threshold' | 'contract_unsigned';
  contractorId: string;
  productionId?: string;
  contractId?: string;
  daysOverdue?: number;
}) {
  trackEvent('Compliance Violation Detected', properties);
}

export function trackComplianceResolved(properties: {
  type: 'w9_submitted' | 'contract_signed' | '1099_filed';
  contractorId: string;
  productionId?: string;
  contractId?: string;
  daysToResolve?: number;
}) {
  trackEvent('Compliance Resolved', properties);
}

// ── Payment Funnel Tracking ───────────────────────────────────────────────────

export function trackInvoiceSent(properties: {
  contractId: string;
  contractorId: string;
  productionId?: string;
  amount: number;
  roleName?: string;
  channel?: 'email' | 'manual';
}) {
  trackEvent('Invoice Sent', properties);
}

export function trackInvoiceViewed(properties: {
  contractId: string;
  contractorId?: string;
  productionId?: string;
}) {
  trackEvent('Invoice Viewed', properties);
}

export function trackPaymentReminded(properties: {
  contractId: string;
  contractorId: string;
  productionId?: string;
  amountDue: number;
  reminderCount?: number;
  channel: 'email' | 'sms' | 'both';
  daysOverdue?: number;
}) {
  trackEvent('Payment Reminder Sent', properties);
}

export function trackPaymentFunnelStep(properties: {
  step: 'invoice_sent' | 'reminder_sent' | 'payment_initiated' | 'payment_completed' | 'payment_failed' | 'overdue';
  contractId: string;
  contractorId: string;
  productionId?: string;
  amount?: number;
  daysElapsed?: number;
}) {
  trackEvent('Payment Funnel Step', properties);
}

export function trackPaymentFailed(properties: {
  contractId: string;
  contractorId: string;
  amount: number;
  reason?: string;
  stripeErrorCode?: string;
}) {
  trackEvent('Payment Failed', properties);
}

// ── PM Workflow Adoption & Alert Tracking ─────────────────────────────────────

export function trackPMAlertSurfaced(properties: {
  type: 'unacknowledged_call_sheets' | 'compliance_gaps' | 'missing_docs';
  count: number;
  productionId?: string;
}) {
  trackEvent('PM Alert Surfaced', properties);
}

export function trackPMReminderSent(properties: {
  type: 'call_sheet' | 'missing_doc' | 'contract' | 'batch';
  contractorId?: string;
  productionId?: string;
  isBulk?: boolean;
  bulkCount?: number;
  source?: 'alert_panel' | 'automation' | 'manual';
}) {
  trackEvent('PM Reminder Sent', properties);
}

export function trackPMBulkReminderSent(properties: {
  type: 'call_sheet' | 'missing_doc' | 'batch_call_sheet';
  count: number;
  productionId?: string;
  role?: string;
  source?: 'alert_panel' | 'automation' | 'batch_panel';
}) {
  trackEvent('PM Bulk Reminder Sent', properties);
}

export function trackCallSheetAcknowledgmentRate(properties: {
  productionId?: string;
  totalSent: number;
  acknowledged: number;
  rate: number;
}) {
  trackEvent('Call Sheet Acknowledgment Rate', properties);
}

export function trackComplianceTrend(properties: {
  productionId?: string;
  overdueCount: number;
  missingDocCount: number;
  unacknowledgedCount: number;
  snapshotDate?: string;
}) {
  trackEvent('Compliance Trend Snapshot', properties);
}

export function trackAutomationRuleCreated(properties: {
  ruleId?: string;
  triggerType: string;
  channels: string[];
  escalationEnabled: boolean;
  maxRetries: number;
  productionId?: string;
}) {
  trackEvent('Automation Rule Created', properties);
}

export function trackAutomationRuleToggled(properties: {
  ruleId: string;
  triggerType: string;
  isActive: boolean;
}) {
  trackEvent('Automation Rule Toggled', properties);
}

export function trackAutomationRuleRun(properties: {
  ruleId?: string;
  triggerType?: string;
  sent: number;
  failed: number;
  isRunAll?: boolean;
}) {
  trackEvent('Automation Rule Executed', properties);
}

export function trackBatchCallSheetSent(properties: {
  productionId: string;
  role?: string;
  recipientCount: number;
  channel: 'email' | 'sms' | 'both';
}) {
  trackEvent('Batch Call Sheet Sent', properties);
}

export function trackPMNavigatedToAutomation(properties: {
  source: string;
}) {
  trackEvent('PM Navigate to Automation', properties);
}

export function trackPMDashboardViewed(properties?: {
  productionId?: string;
  alertCount?: number;
}) {
  trackEvent('PM Dashboard Viewed', properties ?? {});
}

export default mixpanel;
